'use client'

import classnames from 'classnames'
import { IconName } from '@dustin-web/microsite-components/src/icon'
import { Icon } from 'packages/microsite-components/src/icon-library'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { useSiteContext, useTranslations } from 'packages/microsite-components/src/hooks'
import { useTrackingOnScreen } from '@dustin-web/microsite-components/src/on-screen/'

type ServiceCardProps = {
  icon: IconName
  title: string
  content: string
  link: string
  eventName: string
  mainColorClass?: string
  secondaryColorClass?: string
}
type ServiceProps = {
  background?: 'white' | 'gray'
}
const WorkplaceServicesLinks = {
  SE: '/solutions/managed-services/modern-workplace',
  NO: '', // no link
  DK: '/solutions/managed-services/modern-workplace',
  FI: '/solutions/managed-services/modern-workplace',
  NL: '', // no link
  BE: '', // no link
}

const CloudLinks = {
  SE: '/solutions/managed-services/cloud',
  NO: '/tjenester/managed-services/cloud',
  DK: '/solutions/managed-services/cloud',
  FI: '/solutions/managed-services/cloud',
  NL: '/oplossingen/managed-services/cloud',
  BE: '',
}

const SoftwareMarketplaceLinks = {
  SE: '/solutions/software-services/software-marketplace',
  NO: '/tjenester/software-services/software-marketplace',
  DK: '/solutions/software-services/software-marketplace',
  FI: '/solutions/software-services/software-marketplace',
  NL: '/oplossingen/software-services/software-marketplace',
  BE: '',
}

const ManagedServicesLinks = {
  SE: '/solutions/managed-services',
  NO: '/tjenester/managed-services',
  DK: '/solutions/managed-services',
  FI: '/solutions/managed-services',
  NL: '/oplossingen/managed-services',
  BE: '',
}

const onClick = (selectedService: string) => {
  DigitalData.pushSelectPromotion({
    promotion_id: '',
    creative_name: selectedService,
    promotion_name: '',
    creative_slot: 'service_icons',
  })
}

const pushViewPromotion = (eventName: string) => {
  if (eventName) {
    DigitalData.pushViewPromotion({
      creative_name: eventName,
      promotion_name: '',
      creative_slot: 'service_icons',
      promotion_id: '',
    })
  }
}

const ServiceCard = ({
  mainColorClass = '',
  secondaryColorClass = '',
  icon,
  title,
  content,
  link,
  eventName,
}: ServiceCardProps) => {
  const ref = useTrackingOnScreen(() => pushViewPromotion(eventName))

  return (
    <a
      href={link}
      onClick={() => {
        onClick(eventName)
      }}
      className="u-opacity-1 u-radius-8 u-decoration-none d-flex u-flex-1 u-overflow-hidden u-mb-16">
      <Icon
        name={icon}
        className={`i-32 u-px-32 u-py-16 d-flex align-items-center ${mainColorClass}`}
      />
      <div
        className={` u-px-16 u-py-16 d-flex u-flex-1 align-items-center justify-content-between  ${secondaryColorClass}`}>
        <div>
          <h4 ref={ref}>{title}</h4>
          <p className="">{content}</p>
        </div>
        <Icon className={`i-32`} name="arrow-right-small"></Icon>
      </div>
    </a>
  )
}

export const ServicesAndSolutions = ({ background = 'gray' }: ServiceProps) => {
  const { translate } = useTranslations()
  const { sourcingCompany } = useSiteContext()

  return (
    <div
      className={classnames(
        'u-pt-32 u-pb-16',
        {
          'u-bg-white': background === 'white',
        },
        {
          'u-bg-gray-02': background === 'gray',
        }
      )}>
      <div className="container">
        <h2 className="u-fs-28 u-mb-16">{translate('Services_And_Solutions')}</h2>
        <div className="row">
          <div className="col-12 col-md-6 d-flex">
            <ServiceCard
              mainColorClass="t-dark-green"
              secondaryColorClass="u-bg-white"
              eventName="takeback"
              icon="laptop"
              link={WorkplaceServicesLinks[sourcingCompany]}
              title={translate('Services_Workplace')}
              content={translate('Services_Workplace_Text')}
            />
          </div>
          <div className="col-12 col-md-6 d-flex">
            <ServiceCard
              mainColorClass="t-dark-gray"
              secondaryColorClass="u-bg-white"
              eventName="cloud"
              icon="cloud-2"
              link={CloudLinks[sourcingCompany]}
              title={translate('Services_Cloud')}
              content={translate('Services_Cloud_Text')}
            />
          </div>
          <div className="col-12 col-md-6 d-flex">
            <ServiceCard
              mainColorClass="t-dark-brown"
              secondaryColorClass="u-bg-white"
              eventName="software-marketplace"
              icon="diploma"
              link={SoftwareMarketplaceLinks[sourcingCompany]}
              title={translate('Services_Software_Marketplace')}
              content={translate('Services_Software_Marketplace_Text')}
            />
          </div>
          <div className="col-12 col-md-6 d-flex">
            <ServiceCard
              mainColorClass="t-dark-blue"
              secondaryColorClass="u-bg-white"
              eventName="managed-services"
              icon="services"
              link={ManagedServicesLinks[sourcingCompany]}
              title={translate('Services_Managed_Services')}
              content={translate('Services_Managed_Services_Text')}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
